import { useCallback, useEffect, useState } from 'react'

import { getBoughtTogetherItems } from '@/api/clarityLens'
import { ScalrTableColumn } from '@/components/customComponents'
import { formatMetric } from '@/hooks/clarity/util'
import {
  BoughtTogetherItemResponseModel,
  BoughtTogetherItemsModel,
  BoughtTogetherItemsResponseModel,
} from '@/types/clarityLens'
import { getMappedRegionBranches } from '@/utils'

const BASE_BOUGHT_TOGETHER_ITEMS_COLUMNS: ScalrTableColumn[] = [
  {
    header: 'Item Bought Together',
    accessorKey: 'itemName',
    type: 'text',
  },
  {
    header: '# of Orders',
    accessorKey: 'count',
    type: 'number',
  },
  {
    header: '% of Orders',
    accessorKey: 'orderPercentage',
    type: 'percentage',
  },
  {
    header: 'Incremental Revenue',
    accessorKey: 'revenue',
    type: 'money',
  },
]

function getBranchRegion(
  branchName: string,
  regionMap: Map<string, string[]>
): string {
  for (const [region, branches] of regionMap.entries()) {
    if (branches.includes(branchName)) {
      return region
    }
  }
  return 'Undefined Region'
}

function aggregateBranchDataByRegion(
  branchData: Record<string, number>,
  regionBranchMap: Map<string, string[]>
): Record<string, number> {
  const regionData: Record<string, number> = {}
  const regionCounts: Record<string, number> = {}

  // Group branches by region and sum values
  Object.entries(branchData).forEach(([branchName, value]) => {
    const regionName = getBranchRegion(branchName, regionBranchMap)

    if (!regionData[regionName]) {
      regionData[regionName] = 0
      regionCounts[regionName] = 0
    }

    regionData[regionName] += value
    regionCounts[regionName] += 1
  })

  // Calculate averages
  Object.keys(regionData).forEach((regionName) => {
    regionData[regionName] = regionData[regionName] / regionCounts[regionName]
  })

  return regionData
}

function generateBoughtTogetherItemsColumns(
  responseData: BoughtTogetherItemsResponseModel
): ScalrTableColumn[] {
  const baseColumns = [...BASE_BOUGHT_TOGETHER_ITEMS_COLUMNS]
  const { regionBranchMap } = getMappedRegionBranches()

  // Get branch-specific keys from the first item in response
  const firstItem = Object.values(responseData)[0]
  if (firstItem) {
    const { count, revenue, order_pct, ...branchData } = firstItem

    // Get unique regions from branch data
    const regionData = aggregateBranchDataByRegion(branchData, regionBranchMap)

    Object.keys(regionData).forEach((regionName) => {
      baseColumns.push({
        header: `${regionName} %`,
        accessorKey: regionName,
        type: 'percentage',
      })
    })
  }

  return baseColumns
}

export default function useBoughtTogetherItems(
  startDate: string,
  endDate: string,
  branches: string,
  itemGroupName: string,
  sizeFilter: string,
  temperatureFilter: string
) {
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)
  const [boughtTogetherItemsChartData, setBoughtTogetherItemsChartData] =
    useState<BoughtTogetherItemsModel[]>()
  const [boughtTogetherItemsColumns, setBoughtTogetherItemsColumns] = useState<
    ScalrTableColumn[]
  >(BASE_BOUGHT_TOGETHER_ITEMS_COLUMNS)

  const fetchAllData = useCallback(async () => {
    if (!itemGroupName) {
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const { data, columns } = await fetchBoughtTogetherItemsData(
        branches,
        startDate,
        endDate,
        itemGroupName,
        sizeFilter,
        temperatureFilter
      )

      setBoughtTogetherItemsChartData(data)
      setBoughtTogetherItemsColumns(columns)
    } catch (err) {
      setError(
        new Error(
          err instanceof Error ? err.message : 'An unknown error occurred'
        )
      )
    } finally {
      setLoading(false)
    }
  }, [
    branches,
    startDate,
    endDate,
    itemGroupName,
    sizeFilter,
    temperatureFilter,
  ])

  const refetch = useCallback(() => {
    console.log('Refetching bought together items...')
    return fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  return {
    boughtTogetherItemsChartData,
    boughtTogetherItemsColumns,
    loading,
    error,
    refetch,
  }
}

async function fetchBoughtTogetherItemsData(
  branches: string,
  startDate: string,
  endDate: string,
  itemGroupName: string,
  sizeFilter: string,
  temperatureFilter: string
): Promise<{
  data: BoughtTogetherItemsModel[]
  columns: ScalrTableColumn[]
}> {
  try {
    const response = await getBoughtTogetherItems(
      branches,
      startDate,
      endDate,
      itemGroupName,
      sizeFilter,
      temperatureFilter
    )

    // Generate dynamic columns based on response data
    const columns = generateBoughtTogetherItemsColumns(response)

    const data: BoughtTogetherItemsModel[] = Object.entries(response).map(
      ([keyName, value]) => {
        return convertData(keyName, value)
      }
    )

    return {
      data,
      columns,
    }
  } catch (err) {
    throw new Error(
      err instanceof Error ? err.message : 'An unknown error occurred'
    )
  }
}

function convertData(
  keyName: string,
  value: BoughtTogetherItemResponseModel
): BoughtTogetherItemsModel {
  const { count, revenue, order_pct, ...branchData } = value
  const { regionBranchMap } = getMappedRegionBranches()

  const baseModel: BoughtTogetherItemsModel = {
    itemName: keyName,
    count: formatMetric(count, 0),
    revenue: formatMetric(revenue, 2),
    orderPercentage: formatMetric(order_pct, 2),
  }

  // Aggregate branch data by region and add to model
  const regionData = aggregateBranchDataByRegion(branchData, regionBranchMap)

  Object.entries(regionData).forEach(([regionName, regionValue]) => {
    baseModel[regionName] = formatMetric(regionValue, 1)
  })

  return baseModel
}
